import { ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useDesignColors } from '../../hooks/useDesignColors';
import { useMultilingualSettings } from '../../hooks/useMultilingualSettings';
import { supabase } from '../../lib/supabase';

interface Service {
  id: string;
  title: string;
  subtitle: string | null;
  content: string;
  image_url: string | null;
  button_text?: string | null;
  button_url?: string | null;
}

export function ServicesApp() {
  const { chatbotPublicId } = useChatbot();
  const { colors } = useDesignColors();
  const { settings: multilingualSettings } = useMultilingualSettings();
  const [services, setServices] = useState<Service[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isTextExpanded, setIsTextExpanded] = useState(false);

  useEffect(() => {
    if (!chatbotPublicId) return;

    const fetchServices = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('services')
          .select('*')
          .eq('chatbot_public_id', chatbotPublicId)
          .order('position');

        if (error) throw error;

        // Apply translations if available
        let translatedServices = data || [];
        if (multilingualSettings.enabled && multilingualSettings.currentLanguage) {
          const currentLang = multilingualSettings.currentLanguage;
          const translations = multilingualSettings.translations[currentLang];
          
          if (translations?.miniApps) {
            const serviceTranslations = translations.miniApps.find(app => app.id === 'services');
            if (serviceTranslations?.content?.serviceDetails) {
              translatedServices = translatedServices.map(service => {
                const translation = serviceTranslations.content.serviceDetails[service.id];
                if (translation) {
                  return {
                    ...service,
                    title: translation.title || service.title,
                    subtitle: translation.subtitle || service.subtitle,
                    content: translation.content || service.content,
                    button_text: translation.buttonText || service.button_text
                  };
                }
                return service;
              });
            }
          }
        }

        setServices(translatedServices);
      } catch (err) {
        console.error('Error fetching services:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [chatbotPublicId, multilingualSettings]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[500px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
      </div>
    );
  }

  if (services.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[500px] text-center p-4">
        <div className="text-gray-500 mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-gray-500">No configured service</p>
      </div>
    );
  }

  const currentService = services[currentIndex];

  // Function to determine how much text to show based on expansion state
  const getDisplayText = (text: string) => {
    const maxLength = 250; // Increased from 150 to show more content initially
    if (text.length <= maxLength) return text;
    return isTextExpanded ? text : `${text.substring(0, maxLength)}...`;
  };

  return (
    <div className="w-full h-full max-w-4xl mx-auto flex flex-col">
      <div className="relative flex-1 overflow-hidden">
        {currentService.image_url && (
          <div className="w-full h-full absolute inset-0">
            <div className="absolute inset-0 w-full h-full bg-gradient-to-b from-black/10 via-black/60 to-black/90 z-10"></div>
            <img 
              src={currentService.image_url} 
              alt={currentService.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <div className="absolute inset-0 flex flex-col justify-end z-20">
          <div className="p-6 mt-auto space-y-4">
            <h3 className="text-xl font-semibold text-white">{currentService.title}</h3>
            {currentService.subtitle && (
              <p className="text-base text-gray-200">{currentService.subtitle}</p>
            )}
            <div className="space-y-3 max-h-[200px] overflow-y-auto">
              <p className="text-sm text-gray-300">
                {getDisplayText(currentService.content)}
                {currentService.content.length > 250 && (
                  <button
                    onClick={() => setIsTextExpanded(!isTextExpanded)}
                    className="ml-2 text-indigo-400 hover:text-indigo-300 transition-colors text-sm"
                  >
                    {isTextExpanded ? 'See less' : 'Read more'}
                  </button>
                )}
              </p>
              
              {currentService.button_text && currentService.button_url && (
                <a
                  href={currentService.button_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    background: `linear-gradient(to right, ${colors.gradientStart}, ${colors.gradientEnd})`,
                    opacity: 0.9
                  }}
                  className="inline-flex items-center px-4 py-2 rounded-lg text-white text-sm font-medium w-fit hover:opacity-100 transition-opacity mt-2"
                >
                  {currentService.button_text}
                </a>
              )}
            </div>
          </div>
        </div>

        {services.length > 1 && (
          <>
            <button
              onClick={() => {
                setCurrentIndex((prev) => (prev === 0 ? services.length - 1 : prev - 1));
                setIsTextExpanded(false);
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors z-30"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={() => {
                setCurrentIndex((prev) => (prev === services.length - 1 ? 0 : prev + 1));
                setIsTextExpanded(false);
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors z-30"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}